import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { Vector3, type Camera, type Mesh, type Object3D } from 'three/webgpu';

import { usePanelStore } from '../store/panelStore';
import type { PanelCommand, PanelState } from '../store/panelTypes';
import type { Pulse } from './pulse';

type EffectFlag = 'LOOP' | 'PINGPONG' | 'REVERSE' | 'ECHO' | 'FUZZ' | 'FILTER' | 'REVERB';

/** Each effect switch and the button that throws it. */
const EFFECT_BUTTONS: Record<EffectFlag, PanelCommand> = {
  LOOP: 'Button_Loop',
  PINGPONG: 'Button_PingPong',
  REVERSE: 'Button_Reverse',
  ECHO: 'Button_Echo',
  FUZZ: 'Button_Fuzz',
  FILTER: 'Button_Filter',
  REVERB: 'Button_Reverb',
};

const EFFECT_FLAGS = Object.keys(EFFECT_BUTTONS) as EffectFlag[];

/** Where an object sits on the screen, in screen UV with y running down. */
function screenUvOf(object: Object3D, camera: Camera): { x: number; y: number } {
  const projected = object.getWorldPosition(new Vector3()).project(camera);
  return { x: (projected.x + 1) / 2, y: (1 - projected.y) / 2 };
}

/**
 * Sends a ripple out of an effect's button whenever that effect is switched on,
 * however it was switched: a click on the button, its key, or an undo.
 */
export function useEffectPulses(pulse: Pulse, buttons: Map<string, Mesh>): void {
  const camera = useThree((state) => state.camera);

  useEffect(() => {
    const unsubscribes = EFFECT_FLAGS.map((flag) =>
      usePanelStore.subscribe(
        (state: PanelState) => state[flag],
        (on) => {
          const button = buttons.get(EFFECT_BUTTONS[flag]);
          if (on && button) pulse.trigger(screenUvOf(button, camera));
        },
      ),
    );
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [pulse, buttons, camera]);
}
